/**
 * URL-search-param hooks, shared by the batch-detail surfaces that keep a
 * long-running job's handle in the URL.
 *
 * "URL is state of record" means a screen that starts minutes-long work parks
 * its handle there rather than in component state, so a reload reattaches
 * instead of losing the run. Two of them do — the report suites and the batch
 * recompute — and both need the same two things: one write path, and a start
 * key that survives a start whose answer never arrived.
 *
 * Lives beside its callers rather than in `lib/`, which is deliberately free of
 * React and react-router imports. Same placement as `correctionPick.ts`: a
 * shared module local to this folder, tested on its own. The pure half —
 * `withParams` — is in `lib/searchParams.ts`, where a module with no imports
 * belongs and where the surfaces outside this folder can reach it.
 */

import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { withParams } from "../../lib/searchParams";

/**
 * A single search-param write path that composes across one tick.
 *
 * `setSearchParams`'s functional form re-bases on the snapshot captured at
 * render, not on any update queued since (react-router does
 * `nextInit(new URLSearchParams(searchParams))`), so two hooks writing disjoint
 * keys in the same tick do not compose — the second silently drops the first's
 * key. With both suites polling, that means one suite settling at the same
 * moment the other starts can delete the other's job id from the URL, stranding
 * a running render with no handle.
 *
 * The pending edits accumulate, so a re-based write still carries every update
 * queued this tick. They are cleared once the render that observed them lands.
 */
export function useParamPatcher(): (updates: Record<string, string | null>) => void {
  const [params, setParams] = useSearchParams();
  const pending = useRef<Record<string, string | null>>({});
  const applied = useRef(params.toString());
  useEffect(() => {
    // The URL now reflects everything queued before this render, so the
    // accumulator can start over. Compared rather than cleared unconditionally
    // so an unrelated re-render cannot drop an edit queued in the same tick.
    const current = params.toString();
    if (current !== applied.current) {
      applied.current = current;
      pending.current = {};
    }
  }, [params]);
  return useCallback(
    (updates: Record<string, string | null>) => {
      pending.current = { ...pending.current, ...updates };
      const merged = pending.current;
      setParams((prev) => withParams(prev, merged), { replace: true });
    },
    [setParams],
  );
}

/**
 * The idempotency key for one start attempt, parked in the URL until it lands.
 *
 * Starting a job is the one mutation here that must not be replayed under a new
 * key: the backend reserves the slot before scheduling any work and hands back
 * the original `JobRecord` for a repeat of the same key, so a retry after a
 * start whose answer was lost recovers the job the first attempt began. Mint a
 * fresh key per attempt instead and the retry starts a second run — a whole
 * extra DUT fan-out for a recompute, a duplicate render for a suite.
 *
 * The key goes in the URL rather than component state because the answer is
 * exactly what may go missing: a reload, or an unmount while the request is in
 * flight, must not take the key with it.
 *
 * `take()` is falsy-guarded, not null-guarded. `URLSearchParams.get` returns
 * `""` for a key present with no value (`?recomputeStartKey=`), and `"" ?? uuid`
 * is `""` — so a nullish check mints the empty string, which `apiFetch` forwards
 * and `buildHeaders` then drops for being falsy, sending the start with no
 * `Idempotency-Key` at all for the backend to refuse.
 */
export function useStartKey(paramName: string): {
  /** The parked key, or a fresh one — written to the URL before it is used. */
  take: () => string;
  /** The edit that releases it, once a job id has landed. */
  release: () => Record<string, string | null>;
} {
  const [params] = useSearchParams();
  const parked = params.get(paramName);
  const patchParams = useParamPatcher();
  const take = useCallback(() => {
    const key = parked || crypto.randomUUID();
    if (!parked) patchParams({ [paramName]: key });
    return key;
  }, [parked, paramName, patchParams]);
  // Returns the edit rather than applying it, so the caller folds it into the
  // same single write that sets the job id. Two writes would be two history
  // entries, and the second would re-base past the first.
  const release = useCallback(() => ({ [paramName]: null }), [paramName]);
  return { take, release };
}
